import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiGet } from '@/lib/api';
import { useAuth } from './AuthContext';

interface AppSettings {
  id: string;
  name: string;
  domains: string[];
  logo?: string;
  favicon?: string;
  primaryColor?: string;
  status: string;
}

interface AppContextType {
  app: AppSettings | null;
  isLoading: boolean;
}

const AppContext = createContext<AppContextType>({ app: null, isLoading: true });

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [app, setApp] = useState<AppSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { state } = useAuth();

  useEffect(() => {
    if (!state.isAuthenticated) {
      setApp(null);
      setIsLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        const data = await apiGet<AppSettings>('/settings');
        setApp(data);
      } catch {
        // Settings may not exist yet
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [state.isAuthenticated]);

  return (
    <AppContext.Provider value={{ app, isLoading }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
